<!--
    Component Artist
    @author Leander Eyer <leander.eyer@elead.ch>
-->

<template>
	<div class="container">
		<div id="artist-content" class="content" :class="{ hidden: cursor >= 0 }">
			<div class="bar darkText largeText artist-bar">
				<div class="gridLabel bg-artist link" @click="toggleShuffleArtist()">{{ model.getDictionary('nav_artist')}}
					<img v-if="this.shuffleArtist" class='shuffle' :title="model.getDictionary('artist_sort_random')" src='shuffled.png' />
					<img v-else class='shuffle' :title="model.getDictionary('artist_sort_alpha')" src='ordered.png' />
				</div>
				<input
					type="text"
					class="gridTriple lightInput largeText bg-artist-light"
					:placeholder="model.getDictionary('main_search')"
					v-model.trim="searchTerm"
				/>

				<div v-if="searchTerm != ''" class="link artist-close" @click="searchTerm = ''">x</div>
			</div>

			<div v-if="list.length == 0" class='work-none'>{{ model.getDictionary('main_noresult') }}</div>

			<div class="artist-grid" @wheel.prevent="scroll($event.deltaY > 0? 1 : -1)">

				<artist-tile
					v-for="(artist, index) in list.slice(slice, slice + sliceSize)"
					:key="artist.getID()"
					:index="index"
					:artist="artist">
				</artist-tile>				

				<div
					class="artist-grid-up arrow"
					v-if="slice > 0"
					@click="scroll(-1)"
					@mousedown.left="autoscroll('start', -1)"
					@mouseup.left="autoscroll('stop')"
				>&#x25B2;</div>
				<div
					class="artist-grid-down arrow"
					v-if="slice < maxSlice"
					@click.left="scroll(1)"
					@mousedown.left="autoscroll('start', 1)"
					@mouseup.left="autoscroll('stop')"
				>&#x25BC;</div>
			</div>

		</div>

		<artist-detail 
			v-if="cursor >= 0" 
			:artist="list [cursor]"
			:args="args.concat([list [cursor].getURLTerm()])"
			:level="1"></artist-detail>
	
	</div>
</template>

<script>

	import Vue from "vue";
	import { GRID_ROWS } from "./../model/config.js";
	import { model } from "./../model/Model.js";
	import { processSearchTerm } from "./../util.js";
	import { ListWithCursor } from "./../mixin/ListWithCursor.js";
	import { Scrollable } from "./../mixin/Scrollable.js";
	import ArtistDetail from "./ArtistDetail.vue";

	// subcomponent for artist image
	const ArtistWorkTile = Vue.component('artist-work-tile', {
		props: ["work", "index"],
		template: `
			<div class='artist-image zoomable'>
				<img 
					v-if="work.getImages().isImage()"
					class='link'
					:src="work.getImages().getImageURL(previewImageQuality)"
					:title="work.getArtistLabel() + ' - ' + work.getTitle() + ' (' + work.getYear() + ')'"
					@click="$parent.$parent.select(index + $parent.$parent.slice)">
				<div v-else 
					v-html="work.getImages().getMultiMediaHTML(0, 'preview')"
					:title="work.getArtistLabel() + ' - ' + work.getTitle() + ' (' + work.getYear() + ')'"
					@click="$parent.$parent.select(index + $parent.$parent.slice)" ></div>
			</div>`,

		data: function() {
			return {
				previewImageQuality: 'small' 
			}
		},

		created: function() {
			this.previewImageQuality = Vue.prototype.$previewImageQuality;
		}
	});

	// subcomponent for artist
	const ArtistTile = Vue.component('artist-tile', {
		mixins: [ Scrollable ],
		components: { ArtistWorkTile }, 
		props: ["artist", "index"],
		template: `
			<div class="artist-tile">
				<div class='artist-name'><div class='link' @click="$parent.select(index + $parent.slice)">{{ artist.getFullName() }}</div></div>

				<div class='artist-work-grid'>
					<artist-work-tile
						v-for="work in artist.getWorks().slice(slice, slice + sliceSize)"
						:key="work.getID()"
						:work="work"
						:index="index">
					</artist-work-tile>	
					
					<div
						class="artist-tile-left arrow"
						v-if="slice > 0"
						@click.left="scroll(-1)"
						@mousedown.left="autoscroll('start', -1)"
						@mouseup.left="autoscroll('stop')"
					>&#x25C0;</div>
					<div
						class="artist-tile-right arrow"
						v-if="slice < maxSlice"
						@click.left="scroll(1)"
						@mousedown.left="autoscroll('start', 1)"
						@mouseup.left="autoscroll('stop')"
					>&#x25B6;</div>					
				</div>
			</div>`,

		data: function() {
			return {
				// number of cells visible at once
				sliceSize: 5,
				// scroll velocity
				scrollBy: 1
			};
		},

		created: function() {
			// Configure the Grid
			this.sliceSize = Vue.prototype.$device == 'large'? 5 : 2;
			this.maxSlice = Math.max(0, this.artist.getWorks().length - this.sliceSize);
			this.scrollBy = 1;
		}			
	});

	// Main Component
	export default {

		mixins: [ ListWithCursor, Scrollable ],
		components: { ArtistTile, ArtistDetail },

		data: function() {
			return {
				shuffleArtist: model.shuffleArtist,

				// export the model
				model: model,

				// all available elements
				fullList: model.getArtists().getList(),
				fullListAlpha: null,

				// the search term
				searchTerm: "",

				// number of cells visible at once
				sliceSize: GRID_ROWS,
				// scroll velocity
				scrollBy: 1
			};
		},

		methods: {
			// Change Sorting of Artists
			toggleShuffleArtist() {
				this.shuffleArtist = !this.shuffleArtist;
				// remember for the next time we display the page
				this.model.shuffleArtist = this.shuffleArtist;
				// udpate
				this.applyFilter();
			},

			// filter the list of results
			applyFilter: function() {
				let filter = processSearchTerm(this.searchTerm);
				if (this.shuffleArtist == false && this.fullListAlpha == null) {
					this.fullListAlpha = [];
					for (let i = 0; i < this.fullList.length; i++) {
						this.fullListAlpha.push(this.fullList [i]);
					}
					this.fullListAlpha.sort(function(a,b) { return a.getSortName().localeCompare(b.getSortName()) });
				}
				let base = this.shuffleArtist? this.fullList : this.fullListAlpha;
				this.list = base.filter(function(candidate) {
					return candidate.accept(filter);
				});

				// adjust slice
				this.maxSlice = Math.max(0, this.list.length - this.sliceSize);
				this.slice = Math.min(this.maxSlice, this.slice);
			}

		},

		watch: {
			searchTerm: function() {
				this.applyFilter();
			}
		},

		created: function() {
			// Configure the Grid
			this.sliceSize = Vue.prototype.$device == 'large'? GRID_ROWS : 4;
			this.maxSlice = Math.max(0, this.list.length - this.sliceSize);

			// create the list
			this.applyFilter();
		}
	};
</script>


<style>

img.shuffle {
	opacity: 0.6;
	float: right;
	width: 1.2rem;
	height: 1.2rem;
	margin-top: 0.2rem;
	margin-right: 0.4rem;
}

@media (min-width: 1000px) {

	div.artist-close {
		position: absolute;
		right: 3rem;
	}

	div.artist-grid {
		position: relative;
	}

	div.artist-grid-up {
		position: absolute;
		top: 0;
		right: -1rem;

		cursor: pointer;
		user-select: none;
	}

	div.artist-grid-down {
		position: absolute;
		bottom: 0rem;
		right: -1rem;

		cursor: pointer;
		user-select: none;
	}

	div.artist-tile {
		position: relative;
		display: flex;
		flex-direction: row;
		flex-wrap: wrap;
		justify-content: flex-start;

		height: 9rem;
		margin-bottom: 2rem;
	}

	div.artist-name {
		position: relative;
		overflow: hidden;
		width: 9rem;
		height: 9rem;
		margin-right: 2rem;
	}

	div.artist-name div {
		position: absolute;
		bottom: 0;
		right: 0;
		text-align: right;
		margin-right: 0 !important;
	}

	div.artist-work-grid {
		position: relative;
		display: flex;
		flex-direction: row;
		flex-wrap: wrap;
		justify-content: flex-start;

		height: 9rem;
	}

	div.artist-image {
		width: 9rem;
		height: 9rem;
		margin: 0 2rem 0 0;
		overflow: hidden;
	}

	div.artist-image img, div.artist-image div {
		object-fit: cover;
		object-position: center;
		width: 9rem;
		height: 9rem;
	}

	div.artist-tile-left {
		position: absolute;
		top: 3.25rem;
		left: -3rem;
		cursor: pointer;
		user-select: none;
		font-size: 1.5rem;
	}

	div.artist-tile-right {
		position: absolute;
		top: 3.25rem;
		right: -0.75rem;
		cursor: pointer;
		user-select: none;
		font-size: 1.5rem;
	}	

}

@media (max-width: 1000px) {

	div.container, div#artist-content, div.artist-grid {
		height: 100%;
	}

	div.artist-bar {
		height: 4.8rem;
		background-color: white;
		opacity: 1;
	}

	div#artist-content {
		display: block;
	}

	div.artist-close {
		display: inline;
		position: absolute;
		right: 1rem;
		top: 2.7rem;
	}

	div.artist-grid {
		height: calc(100% - 6rem);
		position: relative;
		margin-bottom: -2rem;
	}

	div.artist-grid-up {
		position: absolute;
		top: 0;
		right: -1rem;

		cursor: pointer;
		user-select: none;
	}

	div.artist-grid-down {
		position: absolute;
		bottom: 2rem;
		right: -1rem;

		cursor: pointer;
		user-select: none;
	}

	div.artist-tile {
		position: relative;
		display: flex;
		flex-direction: row;
		flex-wrap: nowrap;
		justify-content: flex-start;

		height: calc(25% - 2rem);
		margin-bottom: 2rem;
	}

	div.artist-name {
		position: relative;
		overflow: hidden;
		width: calc(33%);
		height: 100%;
		margin-right: 2rem;
	}

	div.artist-name div {
		position: absolute;
		bottom: 0;
		right: 0;
		text-align: right;
		margin-right: 0 !important;
	}

	div.artist-work-grid {
		overflow: visible;
		position: relative;
		display: flex;
		flex-direction: row;
		flex-wrap: nowrap;
		justify-content: flex-start;

		width: calc(66%);	
		height: 100%;
	}

	div.artist-image {
		height: 100%;
		width: calc(50% - 2rem);
		margin: 0 2rem 0 0;
		overflow: hidden;
	}

	div.artist-image img, div.artist-image div {
		width: 100%;
		height: 100%;
		object-fit: cover;
		object-position: center;
	}

	div.artist-tile-left {
		position: absolute;
		top: calc(50% - 1rem);
		left: -3rem;
		cursor: pointer;
		user-select: none;
		font-size: 1.5rem;
	}

	div.artist-tile-right {
		position: absolute;
		top: calc(50% - 1rem);
		right: -0.75rem;
		cursor: pointer;
		user-select: none;
		font-size: 1.5rem;
	}	
}

</style>